/** Meta-permissão: no group, restringe o bypass de is_admin nas telas ADMIN_* e CUD mapeados. */
export const ENFORCE_FOR_ADMIN_CODE = 'ENFORCE_FOR_ADMIN';

/** Códigos VIEW das telas do painel /admin. */
export const ADMIN_PANEL_SCREEN_CODES = [
    'ADMIN_HOME_VIEW',
    'ADMIN_COMPANY_VIEW',
    'ADMIN_REQUESTER_VIEW',
    'ADMIN_PROFESSIONAL_VIEW',
    'ADMIN_PROVIDER_VIEW',
    'ADMIN_REGULATOR_VIEW',
    'ADMIN_ADMIN_USER_VIEW',
    'ADMIN_PERMISSION_GROUP_VIEW',
    'ADMIN_VEHICLE_PLAN_VIEW',
    'ADMIN_TYPE_SERVICE_VIEW',
    'ADMIN_MEDICAL_RECORD_VIEW',
    'ADMIN_PERSONAL_BACKGROUND_VIEW',
    'ADMIN_SCALE_VIEW',
    'ADMIN_HEALTH_PLAN_VIEW',
    'ADMIN_CNES_VIEW',
    'ADMIN_SUPPLY_CATALOG_VIEW',
    'ADMIN_PROMPT_VIEW',
    'ADMIN_EQUIPMENT_VIEW',
    'ADMIN_INTEGRATION_VIEW',
    'ADMIN_CRM_CALLS_VIEW',
    'ADMIN_NEWS_VIEW',
    'ADMIN_PRICING_VIEW',
] as const;

export type AdminPanelPermissionCode = (typeof ADMIN_PANEL_SCREEN_CODES)[number];

/**
 * Prefixos clássicos cujo CREATE/UPDATE/DELETE entram no ENFORCE do painel.
 * VIEW dessas áreas continua sendo ADMIN_*_VIEW (separado).
 */
const ADMIN_PANEL_CUD_PREFIXES = [
    'PROMPT',
    'COMPANY',
    'COMPANY_ACTIVITY',
    'PERMISSION_GROUP',
    'VEHICLE_PLAN',
    'TYPE_SERVICE',
    'MEDICAL_RECORD',
    'MEDICAL_RECORD_QUESTION',
    'MEDICAL_RECORD_QUESTION_OPTION',
    'PERSONAL_BACKGROUND',
    'SCALE',
    'HEALTH_PLAN',
    'SUPPLY',
    'INTEGRATION',
    'CONTRACT_TYPE',
    'NEWS',
    'ADMIN',
    'DOCTORS',
    'NURSES',
    'NURSING_TECHS',
    'DRIVERS',
    'PARAMEDICS',
    'COMPANY_ADMIN',
    'MEASURE_EQUIPMENT',
    'VIDEO_EQUIPMENT',
    'ROUTER',
    'STARLINK',
    'SIM_CARD',
    'INDICATOR',
    'TYPE_MEASURE_EQUIPMENT',
    'CRM_CALLS',
    'PRICING',
] as const;

const CUD_SUFFIXES = ['CREATE', 'UPDATE', 'DELETE'] as const;

function buildCudCodes(prefixes: readonly string[]): string[] {
    const codes: string[] = [];
    for (const prefix of prefixes) {
        for (const suffix of CUD_SUFFIXES) {
            codes.push(`${prefix}_${suffix}`);
        }
    }
    return codes;
}

export const ADMIN_PANEL_CUD_CODES = buildCudCodes(ADMIN_PANEL_CUD_PREFIXES);

const ADMIN_PANEL_SCREEN_SET = new Set<string>(ADMIN_PANEL_SCREEN_CODES);
const ADMIN_PANEL_CUD_SET = new Set<string>(ADMIN_PANEL_CUD_CODES);

/** VIEW operacional do Motor de Alocação / cockpit — também entra no ENFORCE. */
const EXTRA_ENFORCED_CODES = new Set<string>(['COMPANY_COCKPIT_VIEW']);

export function isAdminPanelScreenCode(code: string): boolean {
    return ADMIN_PANEL_SCREEN_SET.has(code);
}

/** Códigos que o ENFORCE obriga a checar no group (telas VIEW + CUD mapeados + meta). */
export function isEnforcedAdminPermissionCode(code: string): boolean {
    if (code === ENFORCE_FOR_ADMIN_CODE) {
        return true;
    }
    if (ADMIN_PANEL_SCREEN_SET.has(code)) {
        return true;
    }
    if (EXTRA_ENFORCED_CODES.has(code)) {
        return true;
    }
    return ADMIN_PANEL_CUD_SET.has(code);
}

/** Ordem de preferência para redirect padrão dentro de /admin. */
export const ADMIN_PANEL_DEFAULT_ROUTE_ORDER: Array<{ code: AdminPanelPermissionCode; link: string }> = [
    { code: 'ADMIN_HOME_VIEW', link: '/admin/home' },
    { code: 'ADMIN_CRM_CALLS_VIEW', link: '/admin/crm/calls' },
    { code: 'ADMIN_COMPANY_VIEW', link: '/admin/access/companies' },
    { code: 'ADMIN_REQUESTER_VIEW', link: '/admin/access/requesters' },
    { code: 'ADMIN_PROFESSIONAL_VIEW', link: '/admin/access/users-no-company' },
    { code: 'ADMIN_PROVIDER_VIEW', link: '/admin/access/providers' },
    { code: 'ADMIN_REGULATOR_VIEW', link: '/admin/access/regulators' },
    { code: 'ADMIN_ADMIN_USER_VIEW', link: '/admin/access/admins' },
    { code: 'ADMIN_PERMISSION_GROUP_VIEW', link: '/admin/access/permission-groups' },
    { code: 'ADMIN_VEHICLE_PLAN_VIEW', link: '/admin/vehicle-plans' },
    { code: 'ADMIN_TYPE_SERVICE_VIEW', link: '/admin/services/type_service' },
    { code: 'ADMIN_MEDICAL_RECORD_VIEW', link: '/admin/services/medical_record' },
    { code: 'ADMIN_PERSONAL_BACKGROUND_VIEW', link: '/admin/services/personal_background' },
    { code: 'ADMIN_SCALE_VIEW', link: '/admin/services/scale' },
    { code: 'ADMIN_HEALTH_PLAN_VIEW', link: '/admin/services/health_plan' },
    { code: 'ADMIN_CNES_VIEW', link: '/admin/services/cnes_establishment' },
    { code: 'ADMIN_SUPPLY_CATALOG_VIEW', link: '/admin/supply' },
    { code: 'ADMIN_PROMPT_VIEW', link: '/admin/prompts' },
    { code: 'ADMIN_EQUIPMENT_VIEW', link: '/admin/equipment' },
    { code: 'ADMIN_INTEGRATION_VIEW', link: '/admin/integrations' },
    { code: 'ADMIN_NEWS_VIEW', link: '/admin/news' },
    { code: 'ADMIN_PRICING_VIEW', link: '/admin/pricing' },
];
